/**
 * QorTrace · Public Qelli CTAs — Iter195 (Qelli go-to-market sprint).
 *
 * Five surfaces, one component file, three variants:
 *
 *   <QelliPublicCTA variant="hero">    — Landing-page block (large, with preview).
 *   <QelliPublicCTA variant="inline">  — Pricing / FreeScan / HelpCenter card.
 *   <QelliPublicCTA variant="banner">  — Slim 1-line banner.
 *
 * Plus:
 *   <QelliFloatingPill />              — Global bottom-right pill mounted in App.js
 *                                         when the visitor is NOT logged in.
 *                                         Click opens a preview modal with a sample
 *                                         Q+A and a "Create free account" CTA.
 *
 * The on-site Qelli widget itself stays login-gated (no point in
 * fully unlocking it for un-authed visitors — Qelli's value is tied
 * to having an account so we can save chats, surface citations, and
 * remember the user). The CTAs convert visitors INTO accounts.
 */
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Sparkles, MessageSquare, X, ArrowRight, CheckCircle2 } from "lucide-react";


const SAMPLE_QA = [
  {
    q: "How does the QorTrace Score get computed?",
    a: "Score = 100 − tier_penalty − history_penalty, clamped 0..100. A " +
       "Critical-tier address (exposed pubkey + non-zero balance) starts with a " +
       "60-point penalty before history rolls in. Sources: methodology/how-the-score-is-computed.",
  },
  {
    q: "Is the free tier really free?",
    a: "Yes — no credit card. 1 scan/day, 1 free Standard audit credit on signup, " +
       "Qelli access, Atlas read-only (1 wallet). Sources: billing/plans-and-credits.",
  },
  {
    q: "What is HNDL and why does it matter today?",
    a: "Harvest-Now-Decrypt-Later — an attacker records your encrypted data today " +
       "to decrypt once quantum computers arrive. On-chain pubkeys are forever " +
       "recorded, so the HNDL surface exists right now. Sources: harvest-now-decrypt-later-explained.",
  },
];


// ─── The CTA component ─────────────────────────────────────────────

export function QelliPublicCTA({ variant = "inline", className = "" }) {
  if (variant === "hero")    return <QelliHero   className={className} />;
  if (variant === "banner")  return <QelliBanner className={className} />;
  return <QelliInline className={className} />;
}


function QelliHero({ className = "" }) {
  return (
    <section className={`relative overflow-hidden ${className}`}
             data-testid="qelli-public-hero">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-6">
            <div className="qt-mono text-[11px] inline-flex items-center gap-2 mb-3"
                 style={{ color: "var(--qt-cyan)", letterSpacing: "0.12em" }}>
              <Sparkles size={11} /> QELLI · ADVANCED AI
            </div>
            <h2 className="qt-display"
                style={{
                  fontSize: "clamp(2rem, 4vw, 3.25rem)",
                  lineHeight: 1.05, color: "#fff",
                }}>
              Meet Qelli — A Truly Native AI for PQC.
            </h2>

            {/* Static portrait (no interaction — purely brand surface). */}
            <div className="mt-6 flex items-center gap-5"
                 data-testid="qelli-hero-portrait">
              <img
                src="/assets/qelli-avatar.png"
                alt="Qelli — QorTrace's Advanced AI"
                draggable={false}
                style={{
                  width: 132,
                  height: 132,
                  objectFit: "contain",
                  userSelect: "none",
                  filter: "drop-shadow(0 0 18px rgba(0,229,255,0.45)) drop-shadow(0 0 4px rgba(0,229,255,0.20))",
                }}
              />              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="text-base"
                   style={{ color: "var(--qt-text-2)", lineHeight: 1.6 }}>
                  Qelli is QorTrace's Advanced AI assistant — trained on every QorTrace
                  doc, every methodology version, and post-quantum cryptography as a
                  domain. She'll walk you through pricing, scope audits, and answer
                  compliance questions in seconds — with sources.
                </p>
                <p className="text-sm mt-3"
                   style={{ color: "var(--qt-cyan)" }}>
                  <Sparkles size={12} style={{ display: "inline", marginRight: 4 }} />
                  Free with any QorTrace account, including the free tier.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/signup"
                    data-testid="qelli-hero-signup"
                    className="qt-btn-cyan inline-flex items-center gap-2"
                    style={{ padding: "10px 18px" }}>
                <Sparkles size={14} /> Create free account
              </Link>
              <Link to="/docs/qelli-free-with-account"
                    data-testid="qelli-hero-learn-more"
                    className="qt-mono text-[12px] inline-flex items-center gap-1"
                    style={{
                      padding: "10px 16px", borderRadius: 4,
                      border: "1px solid var(--qt-border)",
                      color: "var(--qt-text-2)", letterSpacing: "0.06em",
                    }}>
                LEARN MORE <ArrowRight size={11} />
              </Link>
            </div>
          </div>

          {/* Right column — Iter238: bullets BELOW chat preview;
              chat preview locked to a stable height so the section
              doesn't jitter while the sample Q+A rotates. */}
          <div className="lg:col-span-6 flex flex-col gap-6">
            <QelliChatPreview />

            <ul
              className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3"
              data-testid="qelli-hero-capabilities"
            >
              {[
                "Walks you through pricing + plan choice",
                "Pre-flight checklists before submitting an audit",
                "Cites every doc she pulls from — no hallucinations",
                "Speaks PQC fluently — HNDL, FIPS 203/204/205, CNSA 2.0",
              ].map((t) => (
                <li
                  key={t}
                  className="text-sm flex items-start gap-2"
                  style={{ color: "var(--qt-text-2)", lineHeight: 1.5 }}
                >
                  <CheckCircle2
                    size={14}
                    style={{
                      color: "var(--qt-cyan)", flexShrink: 0, marginTop: 2,
                    }}
                  />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}


function QelliInline({ className = "" }) {
  return (
    <div className={`qt-card relative overflow-hidden ${className}`}
         data-testid="qelli-public-inline"
         style={{
           padding: "1.5rem 1.75rem",
           background:
             "linear-gradient(135deg, rgba(0,229,255,0.07), rgba(154,255,162,0.04))",
           border: "1px solid rgba(0,229,255,0.20)",
         }}>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="rounded-full flex items-center justify-center"
               style={{
                 width: 44, height: 44,
                 background: "rgba(0,229,255,0.12)",
                 border: "1px solid rgba(0,229,255,0.4)",
               }}>
            <Sparkles size={20} style={{ color: "var(--qt-cyan)" }} />
          </div>
          <div>
            <div className="qt-mono text-[11px]"
                 style={{ color: "var(--qt-cyan)", letterSpacing: "0.10em" }}>
              QELLI · FREE WITH ACCOUNT
            </div>
            <div className="qt-display text-lg mt-0.5" style={{ color: "#fff" }}>
              Got a question? Ask QorTrace's Advanced AI.
            </div>
            <div className="text-xs mt-1" style={{ color: "var(--qt-text-2)" }}>
              Trained on every QorTrace doc + PQC literature. Cites sources.
            </div>
          </div>
        </div>
        <div className="ml-auto">
          <Link to="/signup"
                data-testid="qelli-inline-signup"
                className="qt-btn-cyan inline-flex items-center gap-2"
                style={{ padding: "8px 14px" }}>
            <Sparkles size={13} /> Create free account
          </Link>
        </div>
      </div>
    </div>
  );
}


function QelliBanner({ className = "" }) {
  return (
    <div className={`flex items-center justify-center gap-2 py-2 px-4 ${className}`}
         data-testid="qelli-public-banner"
         style={{
           fontSize: 12,
           background: "rgba(0,229,255,0.05)",
           borderTop: "1px solid rgba(0,229,255,0.20)",
           borderBottom: "1px solid rgba(0,229,255,0.20)",
           color: "var(--qt-text-2)",
         }}>
      <Sparkles size={11} style={{ color: "var(--qt-cyan)" }} />
      <span>
        <strong style={{ color: "#fff" }}>Qelli</strong>, QorTrace's Advanced AI
        trained on PQC — <em>free with any account</em>.
      </span>
      <Link to="/signup"
            data-testid="qelli-banner-signup"
            className="qt-mono"
            style={{ color: "var(--qt-cyan)", letterSpacing: "0.06em" }}>
        SIGN UP →
      </Link>
    </div>
  );
}


// ─── Chat-preview component ─────────────────────────────────────────

function QelliChatPreview() {
  const [idx, setIdx] = useState(0);
  // Rotate the sample Q+A every 6s for marketing motion. Pure visual.
  useEffect(() => {
    const t = setInterval(
      () => setIdx((i) => (i + 1) % SAMPLE_QA.length), 6000,
    );
    return () => clearInterval(t);
  }, []);
  const { q, a } = SAMPLE_QA[idx];

  return (
    <div data-testid="qelli-chat-preview"
         className="flex flex-col"
         style={{
           background: "var(--qt-bg-2)",
           border: "1px solid rgba(0,229,255,0.25)",
           borderRadius: 12,
           padding: "1rem 1.25rem",
           position: "relative",
           overflow: "hidden",
           // Iter238 — fixed height so the section doesn't jitter as
           // the sample Q+A rotates every 6s. Tuned tightly so the
           // right column matches the (now-shorter) left column.
           minHeight: 260,
         }}>
      <div className="absolute inset-0 pointer-events-none"
           style={{
             background:
               "radial-gradient(900px 220px at 80% -40%, rgba(0,229,255,0.10), transparent 60%)",
           }} />
      <div className="qt-mono text-[10px] mb-3"
           style={{ color: "var(--qt-text-3)", letterSpacing: "0.10em" }}>
        QELLI · LIVE PREVIEW
      </div>

      <div className="text-sm" style={{ color: "var(--qt-text-2)" }}>
        <div className="inline-block max-w-[85%] py-2 px-3"
             style={{
               background: "rgba(255,255,255,0.05)",
               borderRadius: "12px 12px 12px 4px",
               color: "#fff",
             }}>
          {q}
        </div>
      </div>

      <div className="text-sm mt-3 text-right">
        <div className="inline-block max-w-[88%] text-left py-2 px-3"
             style={{
               background:
                 "linear-gradient(135deg, rgba(0,229,255,0.18), rgba(0,229,255,0.08))",
               borderRadius: "12px 12px 4px 12px",
               color: "#fff",
               border: "1px solid rgba(0,229,255,0.30)",
             }}>
            <div className="qt-mono text-[10px] mb-1"
                 style={{ color: "var(--qt-cyan)", letterSpacing: "0.10em" }}>
              <Sparkles size={9} style={{ display: "inline", marginRight: 3 }} />
              QELLI
            </div>
            {a}
        </div>
      </div>

      {/* Footer pinned to the bottom regardless of which Q+A is shown. */}
      <div className="flex items-center justify-between"
           style={{
             marginTop: "auto",
             paddingTop: 14,
             borderTop: "1px solid rgba(255,255,255,0.06)",
           }}>
        <div className="flex gap-1">
          {SAMPLE_QA.map((_, i) => (
            <span key={i} aria-hidden="true"
                  style={{
                    width: 6, height: 6, borderRadius: 999,
                    background: i === idx
                      ? "var(--qt-cyan)"
                      : "rgba(255,255,255,0.15)",
                  }} />
          ))}
        </div>
        <span className="qt-mono text-[10px]"
              style={{ color: "var(--qt-text-3)", letterSpacing: "0.08em" }}>
          QELLI ALWAYS CITES SOURCES
        </span>
      </div>
    </div>
  );
}


// ─── Floating global pill (un-authed visitors only) ────────────────

export function QelliFloatingPill({ isAuthed }) {
  const [open, setOpen] = useState(false);
  // Suppress on the on-boarding routes so we don't double up with the
  // logged-in chat widget when the user has just signed up.
  if (isAuthed) return null;

  return (
    <>
      <button onClick={() => setOpen(true)}
              aria-label="Ask Qelli — free with account"
              data-testid="qelli-floating-pill"
              className="qt-mono"
              style={{
                position: "fixed",
                right: 22, bottom: 22, zIndex: 80,
                display: "inline-flex", alignItems: "center", gap: 8,
                padding: "10px 16px 10px 12px",
                borderRadius: 999, cursor: "pointer",
                background:
                  "linear-gradient(135deg, #00E5FF 0%, #6CDDFF 100%)",
                color: "#0A101D",
                fontSize: 12, fontWeight: 700,
                letterSpacing: "0.08em",
                boxShadow: "0 12px 32px rgba(0,229,255,0.35)",
                border: "none",
              }}>
        <Sparkles size={14} style={{ color: "#0A101D" }} /> ASK QELLI
      </button>

      {open && (
        <QelliPreviewModal onClose={() => setOpen(false)} />
      )}
    </>
  );
}


export function QelliPreviewModalInline({ onClose, intentText }) {
  return <QelliPreviewModal onClose={onClose} intentText={intentText} />;
}

function QelliPreviewModal({ onClose, intentText }) {
  return (
    <div data-testid="qelli-preview-modal"
         onClick={onClose}
         style={{
           position: "fixed", inset: 0, zIndex: 1000,
           background: "rgba(0,0,0,0.7)",
           backdropFilter: "blur(6px)",
           display: "flex", alignItems: "center", justifyContent: "center",
           padding: 20,
         }}>
      <div onClick={(e) => e.stopPropagation()}
           className="qt-card"
           style={{
             width: "100%", maxWidth: 560,
             padding: 0, overflow: "hidden",
             border: "1px solid rgba(0,229,255,0.30)",
             // Iter233 fix — make modal fully opaque. The previous
             // gradient-over-undefined-`--qt-bg-2` fell back to
             // transparent and let the underlying landing page bleed
             // through. We stack the cyan/green sheen on top of a
             // solid dark navy so contrast is restored.
             background:
               "linear-gradient(135deg, rgba(0,229,255,0.10), rgba(154,255,162,0.04)), #0B1324",
             boxShadow: "0 20px 60px rgba(0,0,0,0.65)",
           }}>
        <div className="flex items-center justify-between p-4"
             style={{ borderBottom: "1px solid var(--qt-border)" }}>
          <div className="flex items-center gap-2">
            <div className="rounded-full flex items-center justify-center"
                 style={{
                   width: 32, height: 32,
                   background: "rgba(0,229,255,0.18)",
                   border: "1px solid rgba(0,229,255,0.45)",
                 }}>
              <Sparkles size={15} style={{ color: "var(--qt-cyan)" }} />
            </div>
            <div>
              <div className="qt-mono text-[10px]"
                   style={{ color: "var(--qt-cyan)", letterSpacing: "0.10em" }}>
                QELLI · QORTRACE'S ADVANCED AI
              </div>
              <div className="qt-display text-sm" style={{ color: "#fff" }}>
                Trained on PQC + every QorTrace doc.
              </div>
            </div>
          </div>
          <button onClick={onClose}
                  aria-label="Close"
                  data-testid="qelli-preview-modal-close"
                  style={{ color: "var(--qt-text-2)", cursor: "pointer" }}>
            <X size={18} />
          </button>
        </div>

        <div className="p-5">
          {intentText ? (
            <div
              data-testid="qelli-preview-modal-intent"
              className="qt-mono"
              style={{
                fontSize: 11,
                letterSpacing: "0.08em",
                color: "var(--qt-cyan)",
                background: "rgba(0,229,255,0.06)",
                border: "1px solid rgba(0,229,255,0.25)",
                borderRadius: 8,
                padding: "8px 12px",
                marginBottom: 14,
                lineHeight: 1.5,
              }}
            >
              <Sparkles size={11} style={{ display: "inline", marginRight: 4 }} />
              {intentText} — create a free account and Qelli picks up right here.
            </div>
          ) : null}
          <QelliChatPreview />

          <div className="mt-5 text-sm" style={{ color: "var(--qt-text-2)" }}>
            <strong style={{ color: "#fff" }}>Qelli is free with any QorTrace
            account</strong> — including the free tier. Create one in 30 seconds
            and the chat pill unlocks on every authed page.
          </div>

          <div className="flex flex-wrap gap-2 mt-5">
            <Link to="/signup"
                  data-testid="qelli-preview-modal-signup"
                  onClick={onClose}
                  className="qt-btn-cyan inline-flex items-center gap-2 flex-1 justify-center"
                  style={{ padding: "10px 16px" }}>
              <Sparkles size={14} /> Create free account
            </Link>
            <Link to="/signin"
                  data-testid="qelli-preview-modal-login"
                  onClick={onClose}
                  className="qt-mono text-[11px] inline-flex items-center justify-center"
                  style={{
                    padding: "10px 16px", borderRadius: 4,
                    border: "1px solid var(--qt-border)",
                    color: "var(--qt-text-2)", letterSpacing: "0.06em",
                  }}>
              ALREADY HAVE ONE — SIGN IN
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}


export default QelliPublicCTA;
